import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as amplitude from '@amplitude/analytics-browser';

import { AuthRouter } from '../components/AuthRouter';
import { useJWT } from '../contexts/JWTProvider';
import { LoginFormData } from '../types/LoginFormData';
import { createAnalyticsContext } from '../utils/analytics';
import { createLinkState } from '../utils/linkState';
import {
  OnboardErrorDetails,
  handleOnboardError,
  handleOnboardSuccess,
} from '../utils/onboardErrorHandling';

interface UseOnboardFormProps {
  TSP: string;
  serviceName: string;
  token: string;
  fleetCode?: string;
  updatingTSP: boolean;
  serviceID: string;
  magicLink: boolean;
  redirectURISuccess?: string;
  clientIDs: {
    motiveClientID?: string;
    samsaraClientID?: string;
    zubieClientID?: string;
    bouncieClientID?: string;
  };
  onSuccess: () => void;
  setRedirectURI: (uri: string) => void;
  setTspCount: (count: number) => void;
  currentTspCount: number; // Add current TSP count for incrementing
  onSkip?: () => void; // Optional skip handler for escape hatch
}

/**
 * A hook for managing the onboarding form state
 */
const useOnboardForm = ({
  TSP,
  serviceName,
  token,
  fleetCode,
  updatingTSP,
  serviceID,
  magicLink,
  redirectURISuccess,
  clientIDs,
  onSuccess,
  setRedirectURI,
  setTspCount,
  currentTspCount,
  onSkip,
}: UseOnboardFormProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const jwt = useJWT();
  // Form state
  const [isFormValid, setIsFormValid] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [errorDetails, setErrorDetails] = useState<OnboardErrorDetails | null>(
    null
  );

  // Create a compact string that encodes important state for the OAuth flow
  const linkState = useMemo(
    () =>
      createLinkState({
        token,
        updatingTSP,
        magicLink,
        serviceID,
        fleetCode,
      }),
    [token, updatingTSP, magicLink, serviceID, fleetCode]
  );

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData: LoginFormData) => {
      setDisableButton(true);
      setShowError(false);

      // Track form submission
      amplitude.track('Onboarding Form Submitted', {
        tsp: TSP,
        serviceName,
        hasCredentials: !formData.skipCredentials,
      });

      // Credentials tracking is now handled by the analytics system

      // Ensure skipCredentials is a boolean
      const data: LoginFormData = {
        ...formData,
        skipCredentials: !!formData.skipCredentials,
      };

      // Create analytics context for webhook tracking
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        tsp: TSP,
      });

      const authRouter = new AuthRouter({
        TSP,
        serviceName,
        token,
        fleetCode: fleetCode || '',
        updatingTSP,
        serviceID,
        magicLink,
        redirectURISuccess,
        clientIDs,
        analyticsContext, // Pass analytics context for webhook tracking
        linkState,
        onSuccess: () => {
          // Success tracking is now handled by the analytics system
          handleOnboardSuccess(
            {
              tsp: TSP,
              serviceName,
              isUpdating: updatingTSP,
              redirectURI: redirectURISuccess,
            },
            {
              navigate,
              setOnboarded: () => onSuccess(),
              search,
            }
          );
        },
        onError: async (error: any) => {
          await handleOnboardError(
            error,
            {
              tsp: TSP,
              serviceName,
              isUpdating: updatingTSP,
              formData: data,
            },
            {
              navigate,
              setShowError,
              setErrorText,
              setErrorDetails,
              onSkip,
              search,
              analyticsContext, // Pass analytics context for error tracking
            }
          );

          // Error tracking is now handled by the analytics system
          setDisableButton(false);
        },
        setRedirectURI,
        setTspCount,
        currentTspCount, // Pass current count for incrementing
      });

      try {
        await authRouter.handleFormSubmit(data);
      } catch (error) {
        amplitude.track('Onboarding Exception', {
          tsp: TSP,
          serviceName,
          error: String(error),
        });
        setDisableButton(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      TSP,
      serviceName,
      token,
      fleetCode,
      updatingTSP,
      serviceID,
      magicLink,
      redirectURISuccess,
      clientIDs,
      onSuccess,
      setRedirectURI,
      setTspCount,
      currentTspCount,
      // Include missing dependencies
      navigate,
      onSkip,
      search,
      linkState,
      // Include jwt for analytics context
      jwt,
    ]
  ); // Performance optimized: specific JWT properties included for analytics context

  return {
    isFormValid,
    setIsFormValid,
    disableButton,
    showError,
    errorText,
    errorDetails,
    setShowError,
    setErrorText,
    linkState,
    handleFormSubmit,
  };
};

export default useOnboardForm;
